import { useEffect, useState } from "react";
import { Send, X } from "lucide-react";
import { api } from "../api";
import { useAuth, useGuard } from "../auth";
import { subscribe, unsubscribe, onEvent } from "../socket";
import { timeAgo } from "../config";
import { Avatar } from "./cards";

// Shared bottom-sheet comments panel for a piece of content (video / short / etc).
// Uses the same /videos/{id}/comments endpoints as the watch page, so comments
// are shared between web and the mobile app.
export const CommentsSheet = ({ contentId, title, onClose }) => {
  const { user } = useAuth();
  const guard = useGuard();
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => {
    api.get(`/videos/${contentId}/comments`).then((r) => setComments(Array.isArray(r) ? r : r.comments || [])).catch(() => setComments([]));
    subscribe(`video:${contentId}`);
    const off = onEvent("comment", (ev) => {
      if (ev.comment) setComments((c) => (c ? [ev.comment, ...c] : [ev.comment]));
    });
    return () => {
      unsubscribe(`video:${contentId}`);
      off();
    };
  }, [contentId]);

  const send = async (e) => {
    e.preventDefault();
    if (!guard()) return;
    if (!text.trim()) return;
    try {
      const c = await api.post(`/videos/${contentId}/comments`, { text: text.trim() });
      setComments((cs) => [c, ...(cs || [])]);
      setText("");
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="comments-sheet">
      <div
        className="flex h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-[#0c0c12] sm:h-[560px] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <p className="font-heading text-sm font-bold text-white" data-testid="comments-sheet-title">
            Comments{title ? ` · ${title}` : ""}
          </p>
          <button onClick={onClose} className="text-slate-400 transition-colors hover:text-white" data-testid="comments-sheet-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {comments === null ? (
            <p className="py-8 text-center text-sm text-slate-600">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600" data-testid="comments-empty">No comments yet — be the first.</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <Avatar user={c.user || c.author} size={30} />
                <div>
                  <p className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-300">{(c.user || c.author)?.displayName || (c.user || c.author)?.username || "Viewer"}</span> · {timeAgo(c.createdAt)}
                  </p>
                  <p className="mt-1 text-sm text-slate-300">{c.text || c.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <form onSubmit={send} className="flex gap-3 border-t border-white/10 p-4">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => !user && guard()}
            placeholder={user ? "Add a comment…" : "Sign in to comment…"}
            data-testid="comments-sheet-input"
            className="flex-1 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
          />
          <button className="rounded-full bg-crimson p-2.5 text-white transition-colors hover:bg-crimson-hover" data-testid="comments-sheet-send">
            <Send size={15} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentsSheet;
